import { Task } from 'pages/Tasks/types';
import React, { useCallback } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums';
import { useFindManyTaskQuery } from 'shared/types/graphql';
import { TaskDetail } from '../components/TaskDetail';
import { TaskStatusSelector } from '../components/TaskStatusSelector/TaskStatusSelector';

interface UseTaskVisualizerProps {
  taskId?: string;
  canRevertComplete?: boolean;
}

type UseTaskVisualizerReturnType = {
  renderTaskStatusSelector: () => JSX.Element;
  renderTaskDetails: () => JSX.Element;
  getTask: () => Task | undefined;
};

export const useTaskVisualizer = ({
  taskId,
  canRevertComplete,
}: UseTaskVisualizerProps = {}): UseTaskVisualizerReturnType => {
  const { data, loading } = useFindManyTaskQuery({
    variables: {
      where: {
        id: {
          equals: taskId,
        },
      },
    },
    skip: !taskId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  const task = data?.findManyTask?.[0];

  const renderTaskStatusSelector = useCallback(
    (): JSX.Element =>
      !loading && task ? (
        <TaskStatusSelector
          task={task}
          canRevertComplete={canRevertComplete}
          showLabel={true}
        />
      ) : (
        <></>
      ),
    [canRevertComplete, loading, task],
  );

  const renderTaskDetails = useCallback((): JSX.Element => {
    return !loading && task ? <TaskDetail task={task} /> : <></>;
  }, [loading, task]);

  const getTask = useCallback(() => {
    return task;
  }, [task]);

  return { renderTaskStatusSelector, renderTaskDetails, getTask };
};

export default useTaskVisualizer;
